import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Save, Building, Image as ImageIcon, Upload, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_f6178015-27bd-4e5a-b6ad-e233ba6cad25/artifacts/di3lsdme_Logo.jpeg";

const DEFAULT_STATE = {
  firm_name: 'SADANANDA & CO',
  firm_subtitle: 'Legal Consultants',
  contact_person: '',
  address_full: '',
  phone: '',
  email: '',
  logo_url: '',
  letterhead_first_url: '',
  letterhead_other_url: '',
  // Legacy fields kept for backward compatibility with existing data
  address_line1: '',
  address_line2: '',
  address_line3: '',
};

const AdminBrandingPage = () => {
  const [branding, setBranding] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState('');
  const fileInputRef = useRef(null);
  const lhFirstRef = useRef(null);
  const lhOtherRef = useRef(null);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const response = await api.get('/branding');
      // If new address_full is empty but legacy fields exist, seed it for editing convenience
      const data = { ...DEFAULT_STATE, ...response.data };
      if (!data.address_full) {
        const legacy = [data.address_line1, data.address_line2, data.address_line3]
          .filter((l) => l && l.trim())
          .join('\n');
        if (legacy) data.address_full = legacy;
      }
      setBranding(data);
    } catch (error) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/branding', branding);
      toast.success('Branding settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//i.test(file.type)) {
      toast.error('Please choose an image file (PNG, JPG, or WebP).');
      return;
    }
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/branding/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = res.data?.logo_url;
      if (newUrl) {
        setBranding((prev) => ({ ...prev, logo_url: newUrl }));
        toast.success('Logo uploaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = () => {
    setBranding((prev) => ({ ...prev, logo_url: '' }));
    toast.message('Logo cleared — remember to click Save Settings.');
  };

  const handleSlotUpload = async (e, slot, key, ref) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//i.test(file.type)) {
      toast.error('Please choose an image file (PNG, JPG, or WebP).');
      return;
    }
    setUploadingSlot(slot);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`/branding/logo?slot=${slot}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = res.data?.url || res.data?.logo_url;
      if (newUrl) {
        setBranding((prev) => ({ ...prev, [key]: newUrl }));
        toast.success('Image uploaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingSlot('');
      if (ref?.current) ref.current.value = '';
    }
  };

  const imgSrc = (url) => (url ? (url.startsWith('http') ? url : `${BACKEND_URL}${url}`) : null);

  const logoSrc = branding.logo_url
    ? (branding.logo_url.startsWith('http') ? branding.logo_url : `${BACKEND_URL}${branding.logo_url}`)
    : FALLBACK_LOGO;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-64 bg-slate-200 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-branding-page">
      <div>
        <h1 className="text-3xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Branding Settings
        </h1>
        <p className="text-slate-500 mt-1">Customize the DDR header, footer, contact info, and firm logo.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Form */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
              <Building size={20} />
              Firm Details
            </CardTitle>
            <CardDescription>
              Appears on the DDR first-page header (left side). Logo appears top-right of every page.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Contact Person Name</Label>
                <Input
                  value={branding.contact_person || ''}
                  onChange={(e) => setBranding({ ...branding, contact_person: e.target.value })}
                  placeholder="e.g., SADANANDA P NAGANUR"
                  data-testid="contact-person-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Firm Name</Label>
                  <Input
                    value={branding.firm_name || ''}
                    onChange={(e) => setBranding({ ...branding, firm_name: e.target.value })}
                    placeholder="Sadananda & Co."
                    data-testid="firm-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Firm Subtitle</Label>
                  <Input
                    value={branding.firm_subtitle || ''}
                    onChange={(e) => setBranding({ ...branding, firm_subtitle: e.target.value })}
                    placeholder="Legal Consultants"
                    data-testid="firm-subtitle-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Address (multi-line)</Label>
                <Textarea
                  rows={4}
                  value={branding.address_full || ''}
                  onChange={(e) => setBranding({ ...branding, address_full: e.target.value })}
                  placeholder={'No. 54/1, Off 8th Cross, Central Street,\nKumara Park West, Sheshadripuram,\nBengaluru - 560020'}
                  data-testid="address-full-input"
                />
                <p className="text-xs text-slate-500">Each line renders as a separate row in the DDR header.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={branding.phone || ''}
                    onChange={(e) => setBranding({ ...branding, phone: e.target.value })}
                    placeholder="080 23449994"
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={branding.email || ''}
                    onChange={(e) => setBranding({ ...branding, email: e.target.value })}
                    placeholder="office@sadanandalaw.co.in"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>DDR Letterheads</Label>
                <div className="grid grid-cols-1 gap-3">
                  {/* First-page letterhead */}
                  <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50">
                    <div className="w-40 h-16 rounded bg-white border flex items-center justify-center overflow-hidden">
                      {imgSrc(branding.letterhead_first_url)
                        ? <img src={imgSrc(branding.letterhead_first_url)} alt="First-page letterhead" className="max-w-full max-h-full object-contain" />
                        : <span className="text-xs text-slate-400">No image</span>}
                    </div>
                    <div className="flex-1 flex flex-col gap-2">
                      <input ref={lhFirstRef} type="file" accept="image/png, image/jpeg, image/webp"
                        onChange={(e) => handleSlotUpload(e, 'letterhead_first', 'letterhead_first_url', lhFirstRef)}
                        className="hidden" data-testid="lh-first-file-input" />
                      <div className="flex items-center gap-2">
                        <Button type="button" size="sm" variant="outline" disabled={uploadingSlot === 'letterhead_first'}
                          onClick={() => lhFirstRef.current?.click()} data-testid="upload-lh-first-btn">
                          <Upload size={14} className="mr-1.5" />
                          {uploadingSlot === 'letterhead_first' ? 'Uploading…' : (branding.letterhead_first_url ? 'Replace First-page Letterhead' : 'Upload First-page Letterhead')}
                        </Button>
                        {branding.letterhead_first_url && (
                          <Button type="button" size="sm" variant="ghost"
                            onClick={() => setBranding((p) => ({ ...p, letterhead_first_url: '' }))}
                            className="text-red-500 hover:text-red-600" data-testid="remove-lh-first-btn">
                            <X size={14} className="mr-1" /> Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">Full letterhead (with firm name & address). Shown at the top of <b>page 1</b> only.</p>
                    </div>
                  </div>
                  {/* Other-pages logo header */}
                  <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50">
                    <div className="w-40 h-16 rounded bg-white border flex items-center justify-center overflow-hidden">
                      {imgSrc(branding.letterhead_other_url)
                        ? <img src={imgSrc(branding.letterhead_other_url)} alt="Other-pages header" className="max-w-full max-h-full object-contain" />
                        : <span className="text-xs text-slate-400">No image</span>}
                    </div>
                    <div className="flex-1 flex flex-col gap-2">
                      <input ref={lhOtherRef} type="file" accept="image/png, image/jpeg, image/webp"
                        onChange={(e) => handleSlotUpload(e, 'letterhead_other', 'letterhead_other_url', lhOtherRef)}
                        className="hidden" data-testid="lh-other-file-input" />
                      <div className="flex items-center gap-2">
                        <Button type="button" size="sm" variant="outline" disabled={uploadingSlot === 'letterhead_other'}
                          onClick={() => lhOtherRef.current?.click()} data-testid="upload-lh-other-btn">
                          <Upload size={14} className="mr-1.5" />
                          {uploadingSlot === 'letterhead_other' ? 'Uploading…' : (branding.letterhead_other_url ? 'Replace Other-pages Header' : 'Upload Other-pages Header')}
                        </Button>
                        {branding.letterhead_other_url && (
                          <Button type="button" size="sm" variant="ghost"
                            onClick={() => setBranding((p) => ({ ...p, letterhead_other_url: '' }))}
                            className="text-red-500 hover:text-red-600" data-testid="remove-lh-other-btn">
                            <X size={14} className="mr-1" /> Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">Logo-only banner. Shown at the top of <b>pages 2 onward</b>.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Firm Logo</Label>
                <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50">
                  <div className="w-16 h-16 rounded bg-white border flex items-center justify-center overflow-hidden">
                    <img src={logoSrc} alt="Firm logo preview" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png, image/jpeg, image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-file-input"
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={uploadingLogo}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="upload-logo-btn"
                      >
                        <Upload size={14} className="mr-1.5" />
                        {uploadingLogo ? 'Uploading…' : (branding.logo_url ? 'Replace Logo' : 'Upload Logo')}
                      </Button>
                      {branding.logo_url && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={handleRemoveLogo}
                          className="text-red-500 hover:text-red-600"
                          data-testid="remove-logo-btn"
                        >
                          <X size={14} className="mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">PNG, JPG, or WebP. Appears top-right on every DDR page.</p>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#1e3a5f] hover:bg-[#162c46]"
                disabled={saving}
                data-testid="save-branding-btn"
              >
                {saving ? 'Saving...' : (<><Save size={18} className="mr-2" /> Save Settings</>)}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Live Preview */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
              <ImageIcon size={20} />
              Preview
            </CardTitle>
            <CardDescription>How the first page of every DDR / Pending Documents export will look.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border border-slate-200 rounded-lg p-6 bg-white relative min-h-[260px]" data-testid="branding-preview">
              {/* First-page letterhead image */}
              {imgSrc(branding.letterhead_first_url) ? (
                <img src={imgSrc(branding.letterhead_first_url)} alt="First-page letterhead" className="w-full object-contain mb-4" />
              ) : (
                <div className="w-full h-16 border border-dashed border-slate-300 rounded flex items-center justify-center text-xs text-slate-400 mb-4">
                  Upload a first-page letterhead to preview it here
                </div>
              )}
              {/* Date */}
              <p className="font-bold text-slate-900 text-sm mb-3">November 01, 2025</p>
              {/* To + addressee (sample) */}
              <div className="text-xs text-slate-700 mb-3 leading-relaxed">
                <p>To,</p>
                <p>[Client Name]</p>
                <p>[Addressee Address]</p>
              </div>
              {/* Subject */}
              <p className="text-xs text-slate-700 mb-1">Sub: - Due Diligence Report for the below mentioned Schedule Property:-</p>
              <p className="text-center font-bold text-[#1e3a5f] text-sm mt-4">SCHEDULE PROPERTY</p>
              <p className="absolute bottom-2 right-3 text-[10px] text-slate-400">Page 1</p>
            </div>
            <p className="text-xs text-slate-400 mt-2">Pages 2 onward show only the logo-only header banner at the top.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminBrandingPage;
